import React, { PropsWithChildren } from "react";
import styled, { css } from "styled-components";
import { Link, matchPath, useLocation } from "react-router-dom";
import { Bell, CreditCard, LucideIcon, ShieldCheck } from "lucide-react";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import { selectWorkspace } from "@shared/slices/common.slice";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  BASE_BILLING_SETTINGS_PATH,
  IS_WHOP,
  NOTIFICATIONS_SETTINGS_PATH,
  SECURITY_SETTINGS_PATH,
} from "common/constants";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_SECTION_GAP,
  NEW_LOOK_PAGE_GUTTER_VAR,
  phoneMaxWidth,
} from "common/NewLook";
import { useBottomNavigator } from "utils/hooks/misc.tsx";

interface SettingsNavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  link: string;
}

/** The settings sections the current user can open (mirrors the sider). */
const useSettingsNavItems = (): SettingsNavItem[] => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const workspace = useSelector(selectWorkspace);
  const isLimitedUser =
    !!user?.is_sub_user || !!user?.isWhopExperience() || IS_WHOP;

  const items: SettingsNavItem[] = [
    {
      key: "security",
      label: t("page_title.account_and_security"),
      icon: ShieldCheck,
      link: SECURITY_SETTINGS_PATH,
    },
  ];
  if (!isLimitedUser) {
    items.push({
      key: "notifications",
      label: t("labels.notifications"),
      icon: Bell,
      link: NOTIFICATIONS_SETTINGS_PATH,
    });
    if (workspace !== WorkspaceType.ADMIN) {
      items.push({
        key: "billing",
        label: t("page_title.billing"),
        icon: CreditCard,
        link: BASE_BILLING_SETTINGS_PATH,
      });
    }
  }
  return items;
};

/**
 * New-look settings frame: a secondary navigation between the settings
 * sections (a column on desktop, a pill row on narrow screens) next to the
 * section content.
 */
const NewLookSettingsLayout = ({ children }: PropsWithChildren) => {
  const { pathname } = useLocation();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const items = useSettingsNavItems();
  const showNav = items.length > 1;

  return (
    <Layout $isBottomNavigator={isBottomNavigator} $withNav={showNav}>
      {showNav && (
        <Nav>
          {items.map(({ key, label, icon: Icon, link }) => {
            const active = !!matchPath(pathname, { path: link });
            return (
              <NavItem
                key={key}
                to={link}
                $active={active}
                aria-current={active ? "page" : undefined}
                data-testid={`settings-nav-${key}`}
              >
                <Icon size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                <span>{label}</span>
              </NavItem>
            );
          })}
        </Nav>
      )}
      <Content
        $wide={!!matchPath(pathname, { path: BASE_BILLING_SETTINGS_PATH })}
      >
        {children}
      </Content>
    </Layout>
  );
};

export default NewLookSettingsLayout;

const NAV_WIDTH = 220;
const STACKED_BREAKPOINT = 1000;

const Layout = styled.div<{ $isBottomNavigator: boolean; $withNav: boolean }>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$withNav ? `${NAV_WIDTH}px minmax(0, 1fr)` : "minmax(0, 1fr)"};
  align-items: start;
  column-gap: 32px;
  row-gap: 16px;
  padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_SECTION_GAP * 2}px;
  @media (max-width: ${STACKED_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr);
  }
  ${(p) =>
    p.$isBottomNavigator &&
    css`
      padding: 12px 16px 24px;
    `}
`;
const Nav = styled.nav`
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  @media (max-width: ${STACKED_BREAKPOINT}px) {
    position: static;
    flex-direction: row;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
const NavItem = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 12px;
  border-radius: ${(p) => p.theme.radii.control}px;
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 14px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
  svg {
    flex: none;
    color: ${(p) => p.theme.colors.secondaryText};
  }
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
  ${(p) =>
    p.$active &&
    css`
      &&,
      &&:hover {
        color: ${p.theme.colors.primaryBlue};
        background: ${p.theme.colors.lightBlue};
      }
      && svg {
        color: ${p.theme.colors.primaryBlue};
      }
    `}
  @media (max-width: ${STACKED_BREAKPOINT}px) {
    flex: none;
    height: 34px;
    padding: 0 14px;
    font-size: 14px;
    border-radius: ${(p) => p.theme.radii.control}px;
    svg {
      display: none;
    }
    border: ${(p) => p.theme.borders.card};
    ${(p) =>
      p.$active &&
      css`
        border-color: transparent;
      `}
  }
  @media (max-width: ${phoneMaxWidth}) {
    flex: 1 1 auto;
    min-width: 0;
    justify-content: center;
    padding: 0 10px;
    font-size: 13px;
    border-color: transparent;
    ${(p) =>
      !p.$active &&
      css`
        background: ${p.theme.colors.filterChipsColor};
      `}
    span {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  @media (max-width: 359.98px) {
    flex: none;
    padding: 0 8px;
    font-size: 12px;
  }
`;
/** Billing gets room for its side column (880 + 20 gap + 360). */
const Content = styled.div<{ $wide?: boolean }>`
  min-width: 0;
  max-width: ${(p) => (p.$wide ? 1260 : 880)}px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
